import * as React from "react";
import {
    Box,
    Paper,
    Stack,
    Typography,
    List,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Localized, useLocalization } from "@fluent/react";

import type { MyTask } from "../myTasksSlice";
import { MiniStatProgress } from "../utils/ApprovePill";
import { sortByUrgency } from "../utils/taskUrgency";
import { TaskListItem } from "../utils/TaskListItem";

type TaskWidgetProps = {
    title: string;
    badgeColor: string;
    icon: React.ReactNode;
    items: MyTask[];
    onOpen: (task: MyTask) => void;
    emptyTitle?: React.ReactNode;
    emptySubtitle?: React.ReactNode;
    leftColor?: string;
    rightColor?: string;
};

export const TaskWidget: React.FC<TaskWidgetProps> = ({
    title,
    badgeColor,
    icon,
    items,
    onOpen,
    emptyTitle,
    emptySubtitle,
    leftColor = "text.primary",
    rightColor = "text.secondary",
}) => {
    const [now, setNow] = React.useState(() => new Date());
    const { l10n } = useLocalization();

    // Jeden zegar dla wszystkich wierszy.
    React.useEffect(() => {
        const timer = window.setInterval(() => {
            setNow(new Date());
        }, 60_000);

        return () => window.clearInterval(timer);
    }, []);

    const sortedItems = sortByUrgency(items, now);

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                flex: 1,
                minWidth: 0,
                minHeight: 0,
            }}
        >
            {/* Nagłówek */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: "action.hover",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        color: "text.secondary",
                        "& svg": { fontSize: 20 },
                    }}
                >
                    {icon}
                </Box>

                <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{
                        minWidth: 0,
                        overflowWrap: "anywhere",
                    }}
                >
                    {title}
                </Typography>

                <MiniStatProgress
                    value={items.length}
                    color={badgeColor}
                />
            </Stack>

            {/* Zawartość */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                }}
            >
                {sortedItems.length === 0 ? (
                    <Stack
                        spacing={0.75}
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            minHeight: "100%",
                            p: 3,
                            textAlign: "center",
                        }}
                    >
                        <CheckCircleOutlineIcon
                            color="success"
                            sx={{ fontSize: 34 }}
                        />

                        <Typography
                            variant="subtitle2"
                            fontWeight={700}
                        >
                            {emptyTitle ?? l10n.getString("task-widget-empty-title", null, "Wszystko na bieżąco")}
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            {emptySubtitle ?? l10n.getString("task-widget-empty-subtitle", null, "Nie masz tutaj zadań do wykonania.")}
                        </Typography>
                    </Stack>
                ) : (
                    <List disablePadding>
                        {sortedItems.map((task, index) => (
                            <TaskListItem
                                key={task.id}
                                task={task}
                                now={now}
                                onOpen={onOpen}
                                divider={index < sortedItems.length - 1}
                                leftColor={leftColor}
                                rightColor={rightColor}
                            />
                        ))}
                    </List>
                )}
            </Box>
        </Paper>
    );
};